import React, { useEffect, useState } from 'react';
import { BackButton, EditButton } from '../../../../Components/Utils/Buttons';
import { Link, useLocation, useParams } from 'react-router-dom';
import { NormalHeading } from '../../../../Components/Utils/Heading';
import Breadcrumb from '../../../../Components/Utils/Breadcrumb';
import Modal from '../../../../Components/Utils/AttachmentView';

const getFileType = (fileUrl) => {
  if (!fileUrl) return "image";
  const cleanUrl = String(fileUrl).split("?")[0].split("#")[0];
  const ext = cleanUrl.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "pdf";
  if (["png", "jpg", "jpeg", "webp", "gif", "svg"].includes(ext)) return "image";
  return "doc";
};

function ViewLeads() {
  const { leadsViewId } = useParams();
  const location = useLocation();
  const data = location.state?.data;
  const pageIndex = location.state?.pageIndex;
  const searchQuery = location.state?.searchQuery;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [filePathList, setFilePathList] = useState([]);

  useEffect(() => {
    if (
      data?.attachments !== null &&
      data?.attachments !== "None" &&
      data?.attachments !== "[]"
    ) {
      setFilePathList(data?.attachments);
    }
  }, [data]);

  function handleImageClick(img) {
    setSelectedImage(img);
    setIsModalOpen(true);
  }

  const leftDetails = [
    { label: "Name", value: data?.name },
    { label: "Industry", value: data?.industry },
    { label: "Email", value: data?.email },
    { label: "Mobile", value: data?.mobile?.display },
    { label: "Company Size", value: data?.companySize },
    { label: "Contact Person", value: data?.contactPersonName },
    { label: "Conversion Probabilities", value: data?.conversionProbabilities },
    { label: "Lead Source", value: data?.leadSource },
  ];

  const rightDetails = [
    { label: "Address Line 1", value: data?.address1 },
    { label: "Address Line 2", value: data?.address2 },
    { label: "City", value: data?.city },
    { label: "State / Province", value: data?.state },
    { label: "Postal / Zip Code", value: data?.postalCode },
    { label: "Country", value: data?.country },
    { label: "Comments", value: data?.comments },
  ];

  return (
    <div className="w-full h-full py-2 px-3 sm:px-4">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-3">
        <Breadcrumb
          items={[
            { name: "Lead", link: "/marketing/leads" },
            { name: "View" },
          ]}
        />
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Link
            to={'/marketing/leads'}
            state={{ currentPage: pageIndex, searchQuery: searchQuery !== "" ? searchQuery : "" }}
          >
            <BackButton link={true} />
          </Link>
          {leadsViewId && (
            <Link
              to={`/marketing/leads/edit/${leadsViewId}`}
              state={{ data: data, from: `/marketing/leads/view/${leadsViewId}` }}
            >
              <EditButton height="8" />
            </Link>
          )}
        </div>
      </div>

      {/* Main Content Container */}
      <div className="w-full mx-auto h-[calc(100vh-140px)] bg-white rounded-xl shadow-xs border border-slate-200/80 overflow-y-auto p-4 sm:p-5">
        <div className="pb-3 border-b border-gray-100 mb-4">
          <NormalHeading title={"Lead Detail"} />
        </div>

        <div className="flex flex-col lg:flex-row gap-6 w-full items-start">
          {/* Left Column */}
          <div className="w-full lg:w-1/2 flex flex-col gap-1">
            {leftDetails.map((item, idx) => (
              <div
                key={idx}
                className="odd:bg-white even:bg-slate-50/70 w-full flex items-center px-3 py-2.5 rounded-lg text-xs sm:text-sm"
              >
                <p className="w-1/3 sm:w-2/5 font-semibold text-slate-700 shrink-0">
                  {item.label}
                </p>
                <p className="w-2/3 sm:w-3/5 text-slate-600 font-medium break-words pl-2">
                  {item.value || "-"}
                </p>
              </div>
            ))}
          </div>

          <div className="hidden lg:block w-[1px] bg-gray-200/80 self-stretch my-1" />

          {/* Right Column */}
          <div className="w-full lg:w-1/2 flex flex-col gap-1">
            {rightDetails.map((item, idx) => (
              <div
                key={idx}
                className="odd:bg-white even:bg-slate-50/70 w-full flex items-center px-3 py-2.5 rounded-lg text-xs sm:text-sm"
              >
                <p className="w-1/3 sm:w-2/5 font-semibold text-slate-700 shrink-0">
                  {item.label}
                </p>
                <p className="w-2/3 sm:w-3/5 text-slate-600 font-medium break-words pl-2">
                  {item.value || "-"}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Lead Attachment Section */}
        {filePathList && filePathList.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-100">
            <NormalHeading title={"Lead Attachment"} />
            <div className="flex gap-3 items-center flex-wrap mt-3">
              {filePathList.map((imgs, idx) => {
                const type = getFileType(imgs?.file);
                const fileName = imgs?.file ? String(imgs.file).split("/").pop() : "Attachment";
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all max-w-[240px]"
                    onClick={() => handleImageClick(imgs)}
                  >
                    {type === "pdf" ? (
                      <div className="w-8 h-8 rounded bg-red-100 text-red-600 flex items-center justify-center font-bold text-[10px] shrink-0">
                        PDF
                      </div>
                    ) : type === "doc" ? (
                      <div className="w-8 h-8 rounded bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-[10px] shrink-0">
                        DOC
                      </div>
                    ) : (
                      <img src={imgs?.file} alt="attachment" className="w-8 h-8 object-cover rounded shrink-0" />
                    )}
                    <span className="text-xs font-semibold text-slate-700 truncate flex-1" title={fileName}>
                      {fileName}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Attachment View Modal */}
      <Modal show={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {getFileType(selectedImage?.file) === "pdf" || getFileType(selectedImage?.file) === "doc" ? (
          <iframe
            src={selectedImage?.file}
            title="Attachment View"
            className="w-full h-[85vh] rounded-lg border-0"
          />
        ) : (
          <img
            src={selectedImage?.file}
            alt={selectedImage?.file}
            className="max-h-[85vh] mx-auto object-contain rounded-lg"
          />
        )}
      </Modal>
    </div>
  );
}

export default ViewLeads;